import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { StockTicker } from '../components/StockTicker';
import { TrendingUp, Utensils, Bike, ShieldCheck, Smartphone, IndianRupee, ArrowRight, Star, Users, Store, Download } from 'lucide-react';

export const Home = ({ onAuthClick }) => {
  const features = [
    {
      icon: <TrendingUp className="w-8 h-8" />,
      title: 'Food Stock Market',
      description: "India's first platform where food prices fluctuate based on demand - get the best deals!",
    },
    {
      icon: <Utensils className="w-8 h-8" />,
      title: 'Quality Restaurants',
      description: 'Partner with thousands of verified restaurants serving authentic cuisines across India.',
    },
    {
      icon: <Bike className="w-8 h-8" />,
      title: 'Fast Delivery',
      description: 'Lightning-fast delivery by our dedicated fleet of delivery partners.',
    },
    {
      icon: <ShieldCheck className="w-8 h-8" />,
      title: 'Safe & Secure',
      description: 'Your data and payments are protected with enterprise-grade security.',
    },
    {
      icon: <Smartphone className="w-8 h-8" />,
      title: 'Easy to Use',
      description: 'Simple and intuitive app for seamless food ordering experience.',
    },
    {
      icon: <IndianRupee className="w-8 h-8" />,
      title: 'Best Prices',
      description: 'Dynamic pricing ensures you always get the best value for your money.',
    },
  ];

  const stats = [
    { value: '2K+', label: 'Restaurants', icon: <Store className="w-6 h-6" /> },
    { value: '50K+', label: 'Happy Customers', icon: <Users className="w-6 h-6" /> },
    { value: '2K+', label: 'Delivery Partners', icon: <Bike className="w-6 h-6" /> },
    { value: '4.8', label: 'App Rating', icon: <Star className="w-6 h-6" /> },
  ];

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Stock Ticker */}
      <div className="pt-16 md:pt-20">
        <StockTicker />
      </div>

      {/* Hero Section */}
      <section className="fudco-hero-gradient fudco-section relative overflow-hidden">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div className="animate-fade-in-up">
              <div className="inline-flex items-center gap-2 bg-green-100 text-[#2D8B2E] px-4 py-2 rounded-full text-sm font-medium mb-6">
                <TrendingUp className="w-4 h-4" />
                India's First Food Stock Market
              </div>
              <h1 className="fudco-heading-1 text-gray-900 mb-6">
                Invest in Your <span className="text-[#2D8B2E]">Hunger</span>
              </h1>
              <p className="fudco-body-lg mb-8 max-w-lg">
                Experience the revolutionary way of ordering food. Watch prices rise and fall, grab the best deals, and satisfy your cravings with Fudco.
              </p>
              <div className="flex flex-wrap gap-4">
                <Button
                  onClick={onAuthClick}
                  className="bg-[#2D8B2E] hover:bg-[#236c24] text-white rounded-full px-8 py-6 text-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-0.5"
                  data-testid="hero-get-started-btn"
                >
                  Get Started
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                <Link to="/about">
                  <Button
                    variant="outline"
                    className="border-2 border-[#2D8B2E] text-[#2D8B2E] hover:bg-[#2D8B2E] hover:text-white rounded-full px-8 py-6 text-lg font-semibold transition-all duration-300"
                    data-testid="hero-learn-more-btn"
                  >
                    Learn More
                  </Button>
                </Link>
              </div>
            </div>

            {/* Right Content - Food Image */}
            <div className="relative animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
              <div className="relative">
                <img
                  src="https://images.unsplash.com/photo-1565557623262-b51c2513a641?w=800"
                  alt="Delicious Food"
                  className="rounded-3xl shadow-2xl w-full h-[400px] lg:h-[500px] object-cover"
                />
                {/* Floating Cards */}
                <div className="absolute -left-4 top-1/4 bg-white p-4 rounded-2xl shadow-xl animate-pulse-glow">
                  <div className="flex items-center gap-3">
                    <div className="bg-green-100 p-2 rounded-xl">
                      <TrendingUp className="w-6 h-6 text-[#2D8B2E]" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Biryani</p>
                      <p className="font-bold text-[#2D8B2E]">+12% ↑</p>
                    </div>
                  </div>
                </div>
                <div className="absolute -right-4 bottom-1/4 bg-white p-4 rounded-2xl shadow-xl">
                  <div className="flex items-center gap-3">
                    <div className="bg-yellow-100 p-2 rounded-xl">
                      <IndianRupee className="w-6 h-6 text-[#FFD700]" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Save Today</p>
                      <p className="font-bold text-gray-900">₹150 OFF</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="bg-[#2D8B2E] py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center text-white">
                <div className="flex justify-center mb-3">
                  <div className="bg-white/20 p-3 rounded-xl">
                    {stat.icon}
                  </div>
                </div>
                <p className="text-4xl font-bold mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>{stat.value}</p>
                <p className="text-green-100 text-sm">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="fudco-section bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="fudco-heading-2 text-gray-900 mb-4">
              Why Choose <span className="text-[#2D8B2E]">Fudco</span>?
            </h2>
            <p className="fudco-body-lg max-w-2xl mx-auto">
              We're not just another food delivery app. We're revolutionizing how India orders food.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <Card
                key={index}
                className="fudco-card hover-lift group"
                data-testid={`feature-card-${index}`}
              >
                <div className="bg-green-100 text-[#2D8B2E] p-4 rounded-2xl inline-block mb-4 group-hover:bg-[#2D8B2E] group-hover:text-white transition-colors duration-300">
                  {feature.icon}
                </div>
                <h3 className="fudco-heading-3 text-gray-900 mb-3">{feature.title}</h3>
                <p className="fudco-body">{feature.description}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Partner CTA Section */}
      <section className="fudco-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-8">
            {/* Restaurant Partner */}
            <div className="bg-gradient-to-br from-green-50 to-white p-8 md:p-12 rounded-3xl border border-green-100 hover-lift">
              <div className="flex items-center gap-3 mb-6">
                <div className="bg-[#2D8B2E] text-white p-3 rounded-2xl">
                  <Store className="w-8 h-8" />
                </div>
                <h3 className="fudco-heading-3 text-gray-900">Restaurant Partner</h3>
              </div>
              <p className="fudco-body mb-6">
                Join India's first food stock market. Grow your business with dynamic pricing and reach millions of hungry customers.
              </p>
              <Link to="/restaurant-partner">
                <Button className="bg-[#2D8B2E] hover:bg-[#236c24] text-white rounded-full px-6" data-testid="restaurant-partner-cta-btn">
                  Partner With Us
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>

            {/* Delivery Partner */}
            <div className="bg-gradient-to-br from-yellow-50 to-white p-8 md:p-12 rounded-3xl border border-yellow-100 hover-lift">
              <div className="flex items-center gap-3 mb-6">
                <div className="bg-[#FFD700] text-black p-3 rounded-2xl">
                  <Bike className="w-8 h-8" />
                </div>
                <h3 className="fudco-heading-3 text-gray-900">Delivery Partner</h3>
              </div>
              <p className="fudco-body mb-6">
                Be your own boss. Earn on your schedule with competitive payouts and flexible working hours.
              </p>
              <Link to="/delivery-partner">
                <Button className="bg-[#FFD700] hover:bg-[#e6c200] text-black rounded-full px-6" data-testid="delivery-partner-cta-btn">
                  Start Earning
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Download Our Apps Section */}
      <section className="fudco-section bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="fudco-heading-2 text-gray-900 mb-4">
              Download Our <span className="text-[#2D8B2E]">Apps</span>
            </h2>
            <p className="fudco-body-lg max-w-2xl mx-auto">
              Get started with Fudco — download the app and join India's food revolution.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* Delivery Partner App */}
            <Card className="relative overflow-hidden border-2 border-[#2D8B2E]/20 hover:border-[#2D8B2E] transition-all duration-300 hover:shadow-xl group" data-testid="download-delivery-app-card">
              <div className="p-8">
                <div className="flex items-center gap-4 mb-6">
                  <div className="bg-[#2D8B2E] text-white p-4 rounded-2xl shadow-lg group-hover:scale-110 transition-transform duration-300">
                    <Bike className="w-8 h-8" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Delivery Partner</h3>
                    <p className="text-sm text-gray-500">Android App</p>
                  </div>
                </div>
                <p className="text-gray-600 mb-6">
                  Accept orders, navigate deliveries, track your earnings — all in one app. Join our delivery fleet today.
                </p>
                <ul className="space-y-2 mb-8 text-sm text-gray-600">
                  <li className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[#2D8B2E]" /> Real-time order notifications</li>
                  <li className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[#2D8B2E]" /> GPS navigation to pickup & drop</li>
                  <li className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[#2D8B2E]" /> Track daily & weekly earnings</li>
                </ul>
                <a
                  href="https://drive.google.com/file/d/1taA7wSfgwHvvZ_XM_ScZnnn1i7xqA4LY/view?usp=drive_link"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="download-delivery-apk-btn"
                >
                  <Button className="w-full bg-[#2D8B2E] hover:bg-[#236c24] text-white rounded-full py-6 text-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300">
                    <Download className="w-5 h-5 mr-2" />
                    Download Now
                  </Button>
                </a>
              </div>
            </Card>

            {/* Vendor / Restaurant App */}
            <Card className="relative overflow-hidden border-2 border-gray-200 hover:border-[#FFD700] transition-all duration-300 hover:shadow-xl group" data-testid="download-vendor-app-card">
              <div className="p-8">
                <div className="flex items-center gap-4 mb-6">
                  <div className="bg-[#FFD700] text-black p-4 rounded-2xl shadow-lg group-hover:scale-110 transition-transform duration-300">
                    <Store className="w-8 h-8" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Restaurant Partner</h3>
                    <p className="text-sm text-gray-500">Coming Soon</p>
                  </div>
                </div>
                <p className="text-gray-600 mb-6">
                  Manage your restaurant, update menus, accept orders, and grow your business with Fudco.
                </p>
                <ul className="space-y-2 mb-8 text-sm text-gray-600">
                  <li className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[#FFD700]" /> Manage orders in real-time</li>
                  <li className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[#FFD700]" /> Update menu & pricing</li>
                  <li className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[#FFD700]" /> Revenue analytics dashboard</li>
                </ul>
                <Button
                  disabled
                  className="w-full bg-gray-200 text-gray-500 rounded-full py-6 text-lg font-semibold cursor-not-allowed"
                  data-testid="download-vendor-apk-btn"
                >
                  Coming Soon
                </Button>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* App Download CTA */}
      <section className="bg-[#0A2F0A] fudco-section">
        <div className="max-w-7xl mx-auto text-center">
          <h2 className="fudco-heading-2 text-white mb-4">
            Ready to Trade Your <span className="text-[#FFD700]">Hunger</span>?
          </h2>
          <p className="text-green-100 text-lg mb-8 max-w-2xl mx-auto">
            Download the Fudco app and start experiencing India's first food stock market today.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button
              onClick={onAuthClick}
              className="bg-[#FFD700] hover:bg-[#e6c200] text-black rounded-full px-8 py-6 text-lg font-semibold"
              data-testid="download-cta-btn"
            >
              Get Started Now
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};
